/**
 * Sales Orders Toolbar V2 - Comprehensive Filtering with FilterPanel
 *
 * Uses draft state pattern: filters are collected in local state,
 * then applied when user clicks "Apply Filters"
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation, useIsRTL } from '@ufc/core';
import {
  Button,
  Input,
  Badge,
  Checkbox,
  Label,
  BaseDrawer,
  ScrollArea,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DateRangePicker,
  cn,
  Search,
  LayoutGrid,
  List,
  X,
  Filter,
  ChevronDown,
  ChevronUp,
  Sliders,
  CircleDot,
  Package,
  DollarSign,
  Sparkles,
  Loader2,
  Calendar,
  Factory,
} from '@ufc/uikit';
import type {
  SalesOrderStatus,
  SalesOrderSkuOption,
} from '../../../../domain/types';
import { SALES_ORDER_STATUS_OPTIONS } from '../../../../domain/filter-config';

// ============================================
// Types
// ============================================

// Work around react-i18next children typing mismatch across React type roots.
type I18nChild = any;

interface DateRangeValue {
  from: string;
  to: string;
}

interface SalesOrdersToolbarV2Props {
  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;

  // Multi-select filters
  statusFilter: SalesOrderStatus[];
  onStatusChange: (statuses: SalesOrderStatus[]) => void;

  productCodesFilter: string[];
  onProductCodesChange: (codes: string[]) => void;
  productOptions: SalesOrderSkuOption[];
  productOptionsLoading?: boolean;

  deliveryAddressesFilter: string[];
  onDeliveryAddressesChange: (addresses: string[]) => void;
  deliveryAddressOptions: Array<{ value: string; label: string }>;

  // Value range
  minValue?: number;
  maxValue?: number;
  onMinValueChange: (value: number | undefined) => void;
  onMaxValueChange: (value: number | undefined) => void;

  // Date ranges (two separate filters)
  /** @deprecated Use createdDateRange instead */
  dateRange?: DateRangeValue;
  /** @deprecated Use onCreatedDateRangeChange instead */
  onDateRangeChange?: (range: DateRangeValue | undefined) => void;

  // Created date range (createdAt field)
  createdDateRange?: DateRangeValue;
  onCreatedDateRangeChange?: (range: DateRangeValue | undefined) => void;

  // View
  viewMode: 'table' | 'cards';
  onViewModeChange: (mode: 'table' | 'cards') => void;

  // Loading state
  isLoading?: boolean;

  // Filter count for badge
  activeFilterCount?: number;
  onClearFilters?: () => void;

  // Batch apply all filters at once (avoids race conditions)
  onApplyAllFilters?: (filters: {
    status: SalesOrderStatus[];
    productCodes: string[];
    deliveryAddresses: string[];
    factoryIds: string[];
    minValue: number | undefined;
    maxValue: number | undefined;
    dateRange: DateRangeValue | undefined;
    createdDateRange: DateRangeValue | undefined;
  }) => void;
}

// Draft filter state (local to filter panel)
interface FilterDraft {
  status: SalesOrderStatus[];
  productCodes: string[];
  deliveryAddresses: string[];
  minValue: number | undefined;
  maxValue: number | undefined;
  dateRange: DateRangeValue | undefined;
  createdDateRange: DateRangeValue | undefined;
}

// ============================================
// Helper Functions
// ============================================

function buildStatusOptions(
  t: (key: string) => string,
): Array<{ value: string; label: string; color?: string }> {
  return SALES_ORDER_STATUS_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.labelKey),
    color: opt.color,
  }));
}

function buildProductOptions(
  options: SalesOrderSkuOption[],
): Array<{ value: string; label: string; description?: string }> {
  return options.map((opt) => ({
    // Prefer variantId as the filter value so we can
    // reliably match against `variantId` on sales order lines,
    // falling back to `code` when variantId is not available.
    value: String(opt.variantId ?? opt.code),
    label: opt.label,
    description: opt.skuName || opt.variantName,
  }));
}

function formatLocalYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseLocalYmd(value: string): Date | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return undefined;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(monthIndex) ||
    !Number.isFinite(day)
  )
    return undefined;
  return new Date(year, monthIndex, day);
}

function getStatusColor(color?: string): string {
  const colors: Record<string, string> = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    blue: 'bg-blue-500',
    gray: 'bg-gray-400',
    red: 'bg-red-500',
  };
  return colors[color ?? ''] ?? 'bg-gray-400';
}

// ============================================
// Sub-Components
// ============================================

interface CollapsibleSectionProps {
  title: string;
  activeCount: number;
  defaultOpen?: boolean;
  children: I18nChild;
  icon?: I18nChild;
}

function CollapsibleSection({
  title,
  activeCount,
  defaultOpen = false,
  children,
  icon,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="rounded-lg border bg-card"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between p-3 text-sm font-medium hover:bg-muted/50 transition-colors rounded-lg"
        >
          {/* RTL handled by document direction - no flex-row-reverse needed */}
          <span className="flex items-center gap-2.5">
            {icon && <span className="text-muted-foreground">{icon}</span>}
            <span className="font-medium">{title}</span>
            {activeCount > 0 && (
              <Badge className="h-5 min-w-5 px-1.5 text-xs font-semibold bg-primary text-primary-foreground">
                {activeCount}
              </Badge>
            )}
          </span>
          <div
            className={cn(
              'h-6 w-6 rounded-full flex items-center justify-center transition-colors',
              isOpen
                ? 'bg-primary/10 text-primary'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {isOpen ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3 pt-1">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

interface CheckboxListProps {
  options: Array<{
    value: string;
    label: string;
    color?: string;
    description?: string;
  }>;
  selected: string[];
  onChange: (values: string[]) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  maxHeight?: number;
}

function CheckboxList({
  options,
  selected,
  onChange,
  searchable = false,
  searchPlaceholder,
  maxHeight = 200,
}: CheckboxListProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const placeholder = searchPlaceholder || t('common.search');

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(searchLower) ||
        opt.description?.toLowerCase().includes(searchLower),
    );
  }, [options, search]);

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="space-y-3">
      {searchable && (
        <div className="relative">
          {/* Uses logical properties for RTL support */}
          <Search className="absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground start-3" />
          <Input
            type="text"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 text-sm bg-muted/30 border-muted-foreground/20 focus:bg-background ps-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted end-2"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
        </div>
      )}
      <div
        className="space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent"
        style={{ maxHeight: `${maxHeight}px` }}
      >
        {filteredOptions.length === 0 ? (
          <div className="py-6 text-center">
            <div className="text-muted-foreground/50 mb-1">
              <Search className="h-8 w-8 mx-auto" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('common.noOptionsFound')}
            </p>
          </div>
        ) : (
          filteredOptions.map((opt) => {
            const isSelected = selected.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={cn(
                  'flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-[background-color,border-color]',
                  'border border-transparent',
                  isSelected
                    ? 'bg-primary/5 border-primary/20 shadow-sm'
                    : 'hover:bg-muted/50',
                )}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleToggle(opt.value)}
                  className={cn(
                    'shrink-0 transition-[border-color,background-color]',
                    isSelected && 'border-primary',
                  )}
                />
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  {opt.color && (
                    <span
                      className={cn(
                        'w-3 h-3 rounded-full shrink-0 ring-2 ring-background shadow-sm',
                        getStatusColor(opt.color),
                      )}
                    />
                  )}
                  <span
                    className={cn(
                      'text-sm truncate',
                      isSelected
                        ? 'font-medium text-foreground'
                        : 'text-muted-foreground',
                    )}
                  >
                    {opt.label}
                  </span>
                </div>
                {isSelected && (
                  <span className="shrink-0 text-primary">
                    <svg
                      className="h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                )}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

interface NumberRangeProps {
  minValue: number | undefined;
  maxValue: number | undefined;
  onMinChange: (value: number | undefined) => void;
  onMaxChange: (value: number | undefined) => void;
  unit?: string;
  minLabel?: string;
  maxLabel?: string;
}

function NumberRange({
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  unit = '',
  minLabel,
  maxLabel,
}: NumberRangeProps) {
  const { t } = useTranslation();
  const minLabelText = minLabel || t('common.min');
  const maxLabelText = maxLabel || t('common.max');
  // Use native HTML inputs to avoid any UIKit Input component issues
  // Store string values for display, notify parent with parsed numbers
  const [minStr, setMinStr] = useState(() =>
    minValue !== undefined ? String(minValue) : '',
  );
  const [maxStr, setMaxStr] = useState(() =>
    maxValue !== undefined ? String(maxValue) : '',
  );
  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);

  // Sync from props when they change externally (e.g., clear filters)
  // Only update if input is not focused to avoid overwriting user input
  useEffect(() => {
    const newMinStr = minValue !== undefined ? String(minValue) : '';
    const isMinFocused = document.activeElement === minInputRef.current;
    if (!isMinFocused && newMinStr !== minStr) {
      setMinStr(newMinStr);
    }
  }, [minValue]);

  useEffect(() => {
    const newMaxStr = maxValue !== undefined ? String(maxValue) : '';
    const isMaxFocused = document.activeElement === maxInputRef.current;
    if (!isMaxFocused && newMaxStr !== maxStr) {
      setMaxStr(newMaxStr);
    }
  }, [maxValue]);

  const handleMinInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow empty, digits, and one decimal point
    if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
      setMinStr(raw);
      // Immediately notify parent with parsed value
      if (raw === '' || raw === '.') {
        onMinChange(undefined);
      } else {
        const num = parseFloat(raw);
        if (!Number.isNaN(num) && num >= 0) {
          onMinChange(num);
        }
      }
    }
  };

  const handleMaxInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow empty, digits, and one decimal point
    if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
      setMaxStr(raw);
      // Immediately notify parent with parsed value
      if (raw === '' || raw === '.') {
        onMaxChange(undefined);
      } else {
        const num = parseFloat(raw);
        if (!Number.isNaN(num) && num >= 0) {
          onMaxChange(num);
        }
      }
    }
  };

  // Base input classes matching the design system
  const inputClasses = cn(
    'flex h-10 w-full rounded-md border border-input bg-muted/30 px-3 py-2',
    'text-sm font-medium ring-offset-background',
    'placeholder:text-muted-foreground',
    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:bg-background',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'transition-colors',
  );

  return (
    <div className="flex gap-4 items-center">
      <div className="flex-1 space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {minLabelText}
        </Label>
        <div className="relative">
          <input
            ref={minInputRef}
            type="text"
            inputMode="decimal"
            value={minStr}
            onChange={handleMinInput}
            placeholder="0"
            className={cn(inputClasses, unit && 'pe-12')}
          />
          {unit && (
            <span className="absolute top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground bg-muted/50 px-2 py-1 rounded pointer-events-none end-2">
              {unit}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center justify-center w-8 h-8 mt-6 rounded-full bg-muted text-muted-foreground text-xs font-medium">
        →
      </div>
      <div className="flex-1 space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {maxLabelText}
        </Label>
        <div className="relative">
          <input
            ref={maxInputRef}
            type="text"
            inputMode="decimal"
            value={maxStr}
            onChange={handleMaxInput}
            placeholder="∞"
            className={cn(inputClasses, unit && 'pe-12')}
          />
          {unit && (
            <span className="absolute top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground bg-muted/50 px-2 py-1 rounded pointer-events-none end-2">
              {unit}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function SalesOrdersToolbarV2({
  searchValue,
  onSearchChange,
  statusFilter,
  onStatusChange,
  productCodesFilter,
  onProductCodesChange,
  productOptions,
  productOptionsLoading = false,
  deliveryAddressesFilter,
  onDeliveryAddressesChange,
  deliveryAddressOptions,
  minValue,
  maxValue,
  onMinValueChange,
  onMaxValueChange,
  dateRange,
  onDateRangeChange,
  createdDateRange,
  onCreatedDateRangeChange,
  viewMode,
  onViewModeChange,
  isLoading = false,
  activeFilterCount = 0,
  onClearFilters,
  onApplyAllFilters,
}: SalesOrdersToolbarV2Props) {
  const { t } = useTranslation();
  const isRTL = useIsRTL();
  const [isOpen, setIsOpen] = useState(false);

  // Local search state - only submits on Enter or blur
  const [localSearch, setLocalSearch] = useState(searchValue);

  // Sync local search with external value when it changes externally
  useEffect(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);

  // Handle search submission (Enter key or blur)
  const handleSearchSubmit = useCallback(() => {
    if (localSearch !== searchValue) {
      onSearchChange(localSearch);
    }
  }, [localSearch, searchValue, onSearchChange]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearchSubmit();
      }
    },
    [handleSearchSubmit],
  );

  const handleClearSearch = useCallback(() => {
    setLocalSearch('');
    onSearchChange('');
  }, [onSearchChange]);

  // Draft state for filter panel (only applied when user clicks Apply)
  const [draft, setDraft] = useState<FilterDraft>({
    status: statusFilter,
    productCodes: productCodesFilter,
    deliveryAddresses: deliveryAddressesFilter,
    minValue,
    maxValue,
    dateRange,
    createdDateRange,
  });

  // Track previous isOpen state to detect when panel opens
  const prevIsOpenRef = useRef(isOpen);

  // Sync draft with external filter ONLY when panel opens (not on every prop change)
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    // Only sync when panel transitions from closed to open
    if (isOpen && !wasOpen) {
      setDraft({
        status: statusFilter,
        productCodes: productCodesFilter,
        deliveryAddresses: deliveryAddressesFilter,
        minValue,
        maxValue,
        dateRange,
        createdDateRange,
      });
    }
  }, [
    isOpen,
    statusFilter,
    productCodesFilter,
    deliveryAddressesFilter,
    minValue,
    maxValue,
    dateRange,
    createdDateRange,
  ]);

  // Build options
  const statusOptions = useMemo(() => buildStatusOptions(t), [t]);
  const productFilterOptions = useMemo(
    () => buildProductOptions(productOptions),
    [productOptions],
  );
  // Count draft active filters
  const draftActiveCount = useMemo(() => {
    let count = 0;
    if (draft.status.length > 0) count += draft.status.length;
    if (draft.productCodes.length > 0) count += draft.productCodes.length;
    if (draft.deliveryAddresses.length > 0)
      count += draft.deliveryAddresses.length;
    if (draft.minValue !== undefined) count += 1;
    if (draft.maxValue !== undefined) count += 1;
    if (draft.dateRange?.from || draft.dateRange?.to) count += 1;
    if (draft.createdDateRange?.from || draft.createdDateRange?.to) count += 1;
    return count;
  }, [draft]);

  // Handlers
  const handleApplyFilters = useCallback(() => {
    // Ensure numeric values are properly typed (defensive check)
    const safeMinValue =
      typeof draft.minValue === 'number' && Number.isFinite(draft.minValue)
        ? draft.minValue
        : undefined;
    const safeMaxValue =
      typeof draft.maxValue === 'number' && Number.isFinite(draft.maxValue)
        ? draft.maxValue
        : undefined;

    // Use batch handler if available (preferred - avoids race conditions)
    if (onApplyAllFilters) {
      onApplyAllFilters({
        status: draft.status,
        productCodes: draft.productCodes,
        deliveryAddresses: draft.deliveryAddresses,
        factoryIds: [],
        minValue: safeMinValue,
        maxValue: safeMaxValue,
        dateRange: draft.dateRange,
        createdDateRange: draft.createdDateRange,
      });
    } else {
      // Fallback: apply individually (may have race conditions)
      onStatusChange(draft.status);
      onProductCodesChange(draft.productCodes);
      onDeliveryAddressesChange(draft.deliveryAddresses);
      onMinValueChange(safeMinValue);
      onMaxValueChange(safeMaxValue);
      onDateRangeChange?.(draft.dateRange);
      onCreatedDateRangeChange?.(draft.createdDateRange);
    }
    setIsOpen(false);
  }, [
    draft,
    onStatusChange,
    onProductCodesChange,
    onDeliveryAddressesChange,
    onMinValueChange,
    onMaxValueChange,
    onDateRangeChange,
    onCreatedDateRangeChange,
    onApplyAllFilters,
  ]);

  const handleClearDraft = useCallback(() => {
    setDraft({
      status: [],
      productCodes: [],
      deliveryAddresses: [],
      minValue: undefined,
      maxValue: undefined,
      dateRange: undefined,
      createdDateRange: undefined,
    });
  }, []);

  const handleClearAllAndApply = useCallback(() => {
    handleClearDraft();
    onClearFilters?.();
    setIsOpen(false);
  }, [handleClearDraft, onClearFilters]);

  // Build header content for BaseDrawer - RTL handled by document direction
  const filterHeaderContent = (
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
        <Sliders className="h-5 w-5 text-white" />
      </div>
      <div className="flex flex-col items-start">
        <span className="text-lg font-semibold text-white">
          {t('common.filters')}
        </span>
        {draftActiveCount > 0 && (
          <span className="text-xs text-white/70">
            {draftActiveCount} {t('common.filtersActive')}
          </span>
        )}
      </div>
    </div>
  );

  // Build footer content for BaseDrawer - RTL handled by document direction
  const filterFooterContent = (
    <div className="flex items-center gap-3">
      <div className="flex-1" />
      <Button
        variant="ghost"
        onClick={handleClearAllAndApply}
        className="text-muted-foreground"
      >
        <X className="h-4 w-4 me-2" />
        {t('common.clearAll')}
      </Button>
      <Button onClick={handleApplyFilters} className="gap-2 min-w-[120px]">
        <Sparkles className="h-4 w-4" />
        {t('common.apply')}
        {draftActiveCount > 0 && (
          <Badge
            variant="secondary"
            className="h-5 px-1.5 text-xs bg-primary-foreground/20"
          >
            {draftActiveCount}
          </Badge>
        )}
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      {/* Left side: Search + Filters - RTL handled by document direction */}
      <div className="flex flex-1 items-center gap-2">
        {/* Search with submit button - uses logical properties for RTL */}
        <div className="flex items-center gap-0 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none start-3" />
            <Input
              type="text"
              placeholder={t('salesOrders.searchPlaceholder')}
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="h-10 bg-muted/30 border-muted-foreground/20 focus:bg-background transition-colors ps-10 pe-8 rounded-e-none rounded-s-md"
            />
            {localSearch && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted/80 transition-colors end-2"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
          <Button
            onClick={handleSearchSubmit}
            size="default"
            disabled={isLoading}
            className="h-10 px-4 shrink-0 rounded-s-none rounded-e-md"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span className="hidden sm:inline ms-2">{t('common.search')}</span>
          </Button>
        </div>

        {/* Filter Panel Trigger Button */}
        <Button
          variant="outline"
          className="gap-2"
          disabled={isLoading}
          onClick={() => setIsOpen(true)}
          data-testid="sales-orders-filters-button"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Filter className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">{t('common.filters')}</span>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {activeFilterCount}
            </Badge>
          )}
        </Button>

        {/* Filter Panel Drawer */}
        <BaseDrawer
          open={isOpen}
          onOpenChange={setIsOpen}
          side="right"
          isRTL={isRTL}
          title={t('common.filters')}
          headerGradient="from-primary to-primary/80"
          headerContent={filterHeaderContent}
          footerContent={filterFooterContent}
          fixedWidth={520}
          tabletWidth={420}
          mobileWidth="100%"
        >
          <div className="space-y-3 -mx-6 -my-5 px-6 py-4">
            {/* Status Filter */}
            <CollapsibleSection
              title={t('salesOrders.filterByStatus')}
              activeCount={draft.status.length}
              defaultOpen
              icon={<CircleDot className="h-4 w-4" />}
            >
              <CheckboxList
                options={statusOptions}
                selected={draft.status}
                onChange={(values) =>
                  setDraft((prev) => ({
                    ...prev,
                    status: values as SalesOrderStatus[],
                  }))
                }
              />
            </CollapsibleSection>

            {/* Products Filter */}
            <CollapsibleSection
              title={t('salesOrders.filterByProduct')}
              activeCount={draft.productCodes.length}
              icon={<Package className="h-4 w-4" />}
            >
              {productOptionsLoading ? (
                <div className="py-6 text-center">
                  <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {t('common.loading')}...
                  </p>
                </div>
              ) : productFilterOptions.length === 0 ? (
                <div className="py-6 text-center">
                  <Package className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {t('salesOrders.noProductsAvailable')}
                  </p>
                </div>
              ) : (
                <CheckboxList
                  options={productFilterOptions}
                  selected={draft.productCodes}
                  onChange={(values) =>
                    setDraft((prev) => ({ ...prev, productCodes: values }))
                  }
                  searchable
                  searchPlaceholder={t('salesOrders.searchProducts')}
                  maxHeight={180}
                />
              )}
            </CollapsibleSection>

            {/* Created Date Range Filter */}
            <CollapsibleSection
              title={t('salesOrders.filterByCreatedDate')}
              activeCount={
                draft.createdDateRange?.from || draft.createdDateRange?.to
                  ? 1
                  : 0
              }
              icon={<Calendar className="h-4 w-4" />}
            >
              <DateRangePicker
                dateRange={
                  draft.createdDateRange
                    ? {
                        from: draft.createdDateRange.from
                          ? parseLocalYmd(draft.createdDateRange.from)
                          : undefined,
                        to: draft.createdDateRange.to
                          ? parseLocalYmd(draft.createdDateRange.to)
                          : undefined,
                      }
                    : undefined
                }
                onDateRangeChange={(range) => {
                  if (range?.from || range?.to) {
                    setDraft((prev) => ({
                      ...prev,
                      createdDateRange: {
                        from: range.from ? formatLocalYmd(range.from) : '',
                        to: range.to ? formatLocalYmd(range.to) : '',
                      },
                    }));
                  } else {
                    setDraft((prev) => ({
                      ...prev,
                      createdDateRange: undefined,
                    }));
                  }
                }}
                isRTL={isRTL}
                placeholder={t('salesOrders.selectCreatedDateRange')}
                presets={[
                  {
                    label: t('dateRangePicker.thisWeek'),
                    value: () => {
                      const now = new Date();
                      const start = new Date(now);
                      start.setDate(now.getDate() - now.getDay());
                      const end = new Date(start);
                      end.setDate(start.getDate() + 6);
                      return { from: start, to: end };
                    },
                  },
                  {
                    label: t('dateRangePicker.thisMonth'),
                    value: () => {
                      const now = new Date();
                      const start = new Date(
                        now.getFullYear(),
                        now.getMonth(),
                        1,
                      );
                      const end = new Date(
                        now.getFullYear(),
                        now.getMonth() + 1,
                        0,
                      );
                      return { from: start, to: end };
                    },
                  },
                  {
                    label: t('dateRangePicker.last30Days'),
                    value: () => {
                      const end = new Date();
                      const start = new Date();
                      start.setDate(end.getDate() - 30);
                      return { from: start, to: end };
                    },
                  },
                  {
                    label: t('dateRangePicker.last90Days'),
                    value: () => {
                      const end = new Date();
                      const start = new Date();
                      start.setDate(end.getDate() - 90);
                      return { from: start, to: end };
                    },
                  },
                ]}
                className="w-full"
              />
            </CollapsibleSection>
          </div>
        </BaseDrawer>

        {/* Active filter count badge (visible when filters are applied) */}
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <X className="h-4 w-4" />
            <span className="hidden sm:inline">{t('common.clearFilters')}</span>
            <span className="sm:hidden">({activeFilterCount})</span>
          </Button>
        )}
      </div>

      {/* Right side: View Toggle - RTL handled by document direction */}
      <div className="flex items-center gap-2">
        {/* View Toggle - uses logical border-radius (rounded-s/e-none) */}
        <div className="flex items-center border rounded-md">
          <Button
            variant={viewMode === 'table' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('table')}
            className="rounded-e-none"
            title={t('common.tableView')}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('cards')}
            className="rounded-s-none"
            title={t('common.cardView')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
